/**
 * Meal plan `includedOptions` (the MPH-696 pricing contract) mapped to copy keys, in display order.
 *
 * The same `boardClass` can cover different meals depending on the supplier — All Inclusive and
 * All Inclusive Plus both map to ALL_INCLUSIVE, the difference lives in `includedOptions` — so the
 * room area lists what the selected plan actually covers behind an info icon next to the meal plan
 * name (MPH-946).
 *
 * Codes are grouped by meal (breakfast → lunch → dinner → drinks) so a plan always reads in the
 * same order whatever order the supplier sent them in. LUNCH_OR_DINNER is a genuine half-board
 * case (Hotelbeds HB, GH 22, Disney plans): the supplier sells one main meal without saying which,
 * so it gets its own line rather than being shown as lunch or as dinner. `OT` (other) says nothing
 * about the meals and is dropped by the adapter, like any code this map does not know — a plan
 * whose options are all unknown therefore arrives with an empty list and renders no info icon.
 */
export const MEAL_PLAN_INCLUDED_OPTION_KEYS = {
  BREAKFAST: 'vacation.select.rooms.meal_plan_included.breakfast',
  CONTINENTAL_BREAKFAST: 'vacation.select.rooms.meal_plan_included.continental_breakfast',
  BUFFET_BREAKFAST: 'vacation.select.rooms.meal_plan_included.buffet_breakfast',
  REGIONAL_BREAKFAST: 'vacation.select.rooms.meal_plan_included.regional_breakfast',
  BREAKFAST_PREMIUM: 'vacation.select.rooms.meal_plan_included.breakfast_premium',
  LUNCH: 'vacation.select.rooms.meal_plan_included.lunch',
  LUNCH_BUFFET: 'vacation.select.rooms.meal_plan_included.lunch_buffet',
  LUNCH_REGIONAL: 'vacation.select.rooms.meal_plan_included.lunch_regional',
  LUNCH_SPECIAL: 'vacation.select.rooms.meal_plan_included.lunch_special',
  LUNCH_OR_DINNER: 'vacation.select.rooms.meal_plan_included.lunch_or_dinner',
  DINNER: 'vacation.select.rooms.meal_plan_included.dinner',
  DINNER_BUFFET: 'vacation.select.rooms.meal_plan_included.dinner_buffet',
  DINNER_REGIONAL: 'vacation.select.rooms.meal_plan_included.dinner_regional',
  DINNER_SPECIAL: 'vacation.select.rooms.meal_plan_included.dinner_special',
  BEVERAGES: 'vacation.select.rooms.meal_plan_included.beverages',
  SOFT_BEVERAGES: 'vacation.select.rooms.meal_plan_included.soft_beverages',
  PREMIUM_DRINKS: 'vacation.select.rooms.meal_plan_included.premium_drinks',
} as const;

export type MealPlanIncludedOption = keyof typeof MEAL_PLAN_INCLUDED_OPTION_KEYS;

/** Heading of the included-options list, also the accessible name of the info icon. */
export const MEAL_PLAN_INCLUDED_OPTIONS_TITLE_KEY = 'vacation.select.rooms.meal_plan_included.title';

const DISPLAY_ORDER = Object.keys(MEAL_PLAN_INCLUDED_OPTION_KEYS) as MealPlanIncludedOption[];

/**
 * Any `includedOptions` payload — the raw travel-api field (an array of codes, `null`, or absent)
 * or an already adapted one — reduced to the options this UI can name, deduplicated and in display
 * order. Codes the map does not know (`OT`, supplier-specific boards) are dropped by the rooms
 * adapter rather than on the client: no consumer reads them (the debug rooms inspector dumps the
 * raw pricing response, not the adapted rooms), so keeping them would only ship dead data.
 *
 * The readers below go through it too, rather than filtering the adapted list themselves. On an
 * adapted payload that is a no-op, but it is what makes them agree with each other on a payload
 * that skipped the adapter: suppliers repeat the same meal under two spellings, and a list counted
 * without deduplicating would read as two options where there is only one (MPH-1067).
 */
export function normalizeMealPlanIncludedOptions(includedOptions: unknown): MealPlanIncludedOption[] {
  if (!Array.isArray(includedOptions)) return [];

  const codes = new Set(
    includedOptions
      .filter((option): option is string => typeof option === 'string')
      .map((option) => option.trim().toUpperCase())
  );

  return DISPLAY_ORDER.filter((option) => codes.has(option));
}

/**
 * Copy keys for the plan's options, in display order — one row per meal, whatever the payload's
 * order and however many spellings it sent the meal under.
 */
export function getMealPlanIncludedOptionKeys(includedOptions: readonly string[] | undefined): string[] {
  return normalizeMealPlanIncludedOptions(includedOptions).map((option) => MEAL_PLAN_INCLUDED_OPTION_KEYS[option]);
}

/**
 * The one included option a board class name already states by itself. The ACCOMODATION_AND_BREAKFAST
 * label is "Breakfast", so a plan whose only option is BREAKFAST repeats the name and nothing more —
 * while the same plan with CONTINENTAL_BREAKFAST does say something new (MPH-1067). No other board
 * class names a single meal: half board, full board and all-inclusive cover several, so whatever
 * single option they arrive with is always narrower than their label and worth showing.
 */
const SELF_EXPLANATORY_SINGLE_OPTION: Partial<Record<BoardClassType, MealPlanIncludedOption>> = {
  ACCOMODATION_AND_BREAKFAST: 'BREAKFAST',
};

/**
 * Whether the plan's options are worth an info icon next to its name (MPH-1067). They are when the
 * plan lists more than one option, or when its single option is not the one the plan's own name
 * already states — a list that only repeats the meal plan name is not worth an icon to open it.
 * Counting the normalized list rather than the payload's own entries is what keeps that rule honest
 * on a payload the adapter has not been through (rolling deploy): unknown codes cannot open a modal
 * on options this UI cannot name, and one meal sent twice cannot pass itself off as two options.
 */
export function hasMealPlanIncludedOptions(
  mealPlan: { boardClass: BoardClassType; includedOptions?: readonly string[] } | undefined
): boolean {
  if (!mealPlan) return false;

  const options = normalizeMealPlanIncludedOptions(mealPlan.includedOptions);

  if (options.length > 1) return true;
  return options.length === 1 && options[0] !== SELF_EXPLANATORY_SINGLE_OPTION[mealPlan.boardClass];
}
